import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  FormGroup,
  TextField,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { SelectableKeyValueItem } from "src/core/Models/Core.interface";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import * as Constants from "./../../../core/Constants";
import { CorePropsInterfaces, DTOs } from "src/core/Models";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { isFinalorOutdatedVersion } from "src/utils";
import { AppContext } from "src/contexts/AppContext";
import { useCallback, useContext, useMemo } from "react";

function QuestionMultipleSelectInput({
  question,
  isDisabled,
  onChange,
}: CorePropsInterfaces.QuestionInputProps) {
  const { globalState } = useContext(AppContext);
  const { t } = useTranslation();

  const userValueToArray =
    Array.isArray(question.data.userValue) && question.data.userValue.length > 0
      ? question.data.userValue
      : [Constants.HelpfulConstants.PleaseSelect];
  const isFinalOrOutdatedVersion = isFinalorOutdatedVersion(globalState);

  // Identity must stay stable across renders: MUI compares `value` by reference to
  // decide whether to reset the input, and a fresh object wipes what the user typed.
  const options: SelectableKeyValueItem[] = useMemo(() => {
    const selectedValues: unknown[] = Array.isArray(question.data.userValue)
      ? question.data.userValue
      : [];
    const selectedKeys = new Set<string>();
    selectedValues.forEach((userValue) => {
      if (userValue) {
        selectedKeys.add(userValue.toString());
      }
    });
    return question.data.options.map((option) => ({
      ...option,
      isSelected: selectedKeys.has(option.key),
    }));
  }, [question.data.options, question.data.userValue]);

  const hasDefaultOptions = !!question.data.inputConstraints?.hasDefaultOptions;

  const getOptionLabel = useCallback(
    (option: SelectableKeyValueItem) =>
      hasDefaultOptions ? t(`Options.${option.key}`) : option.value ?? "",
    [hasDefaultOptions, t]
  );

  // Match on the person's name only, not the "<email>, [BusinessUnit]" decoration.
  const filterOptions = useMemo(
    () =>
      createFilterOptions<SelectableKeyValueItem>({
        stringify: (option) =>
          hasDefaultOptions
            ? t(option.value ?? "")
            : (option.value ?? "").replace(/<[^>]*>|\[[^\]]*\]/g, ""),
      }),
    [hasDefaultOptions, t]
  );

  const onSelectedValueChange = (selectedOption: string, index: number) => {
    const updatedUserValueArray = [...userValueToArray];
    updatedUserValueArray[index] = selectedOption;

    const newQuestionDTO: DTOs.QuestionDTO = {
      ...question,
      data: {
        ...question.data,
        userValue: updatedUserValueArray as unknown as string,
      },
      state: {
        ...question.state,
        isFilled: question.state.isFilled,
      },
    };

    onChange && onChange(newQuestionDTO);
  };

  const addEntry = () => {
    const maxEntrances = !!question.data.inputConstraints.maxEntrances
      ? question.data.inputConstraints.maxEntrances
      : 10;
    if (userValueToArray.length < maxEntrances && !isFinalOrOutdatedVersion) {
      const newValue = Constants.HelpfulConstants.PleaseSelect;

      const newArray = [...userValueToArray, newValue];
      const newQuestionDTO: DTOs.QuestionDTO = {
        ...question,
        data: {
          ...question.data,
          userValue: newArray as unknown as string,
        },
        state: {
          ...question.state,
          isFilled: false,
        },
      };
      onChange && onChange(newQuestionDTO);
    }
  };

  const deleteEntry = (index: number) => {
    const newUserValue = [
      ...userValueToArray.slice(0, index),
      ...userValueToArray.slice(index + 1),
    ];

    const newQuestionDTO: DTOs.QuestionDTO = {
      ...question,
      data: {
        ...question.data,
        userValue: newUserValue as unknown as string,
      },
    };

    onChange && onChange(newQuestionDTO);
  };

  return (
    <FormGroup>
      {userValueToArray.map((selectedValue, index) => (
        <div key={index} className="gt-multipleSelectContainer">
          <Autocomplete
            key={index}
            data-testid={question.data.code}
            className="gt-Autocomplete"
            disablePortal
            disabled={isDisabled}
            options={options}
            getOptionLabel={getOptionLabel}
            getOptionKey={(option) => option.key}
            filterOptions={filterOptions}
            getOptionDisabled={(option) => option.isSelected === true}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            value={
              options.find(
                (option) => option.key === (selectedValue as unknown as string)
              ) ?? null
            }
            onChange={(syntheticBaseEvent, evt) => {
              const selectedOption =
                evt?.key || Constants.HelpfulConstants.PleaseSelect;
              onSelectedValueChange(selectedOption, index);
            }}
            renderOption={(props, option) => {
              const { key, ...restProps } = props;
              return (
                <Box
                  component="li"
                  key={key}
                  {...restProps}
                  data-testid={`option_question${question.data.code}_option${option.key}`}
                >
                  {!question.data.inputConstraints?.hasDefaultOptions
                    ? option.value
                    : t(option.value)}
                </Box>
              );
            }}
            renderInput={(params) =>
              question.data.inputConstraints?.hasVisibleTooltip ? (
                <Tooltip
                  title={
                    typeof selectedValue === "string"
                      ? t(`Options.${selectedValue}`)
                      : ""
                  }
                  placement="top"
                >
                  <TextField
                    {...params}
                    placeholder={t("Options.PleaseSelect")}
                  />
                </Tooltip>
              ) : (
                <TextField
                  {...params}
                  placeholder={t("Options.PleaseSelect")}
                />
              )
            }
          />

          {userValueToArray.length > 1 && !isFinalOrOutdatedVersion && (
            <Tooltip
              title={t(`Forms.Button.${question.data.code}.Remove`)}
              placement="right"
            >
              <Button
                color="tertiary"
                className="gt-multipleEntriesInputContainerButton gt-multipleEntriesInputContainerButton--delete"
                onClick={() => deleteEntry(index)}
              >
                {!question.data.inputConstraints?.hasDefaultOptions ? (
                  <PersonRemoveIcon />
                ) : (
                  <ClearIcon />
                )}
              </Button>
            </Tooltip>
          )}
        </div>
      ))}
      <Tooltip
        title={t(`Forms.Button.${question.data.code}.Add`)}
        placement="left"
      >
        <div
          className={`gt-multipleEntriesInputContainerButton 
                        ${
                          userValueToArray.length <
                            question.data.inputConstraints?.maxEntrances &&
                          !isFinalOrOutdatedVersion
                            ? " gt-multipleEntriesInputContainerButton--add"
                            : " gt-multipleEntriesInputContainerButton--disabled"
                        }
                      }`}
        >
          {!question.data.inputConstraints?.hasDefaultOptions ? (
            <PersonAddAlt1Icon onClick={addEntry} />
          ) : (
            <AddIcon onClick={addEntry} />
          )}
        </div>
      </Tooltip>
    </FormGroup>
  );
}

export default QuestionMultipleSelectInput;
